import React, {useEffect, useRef, useState} from "react";
import {hopnLanguageArray} from "@/utils/functions.js";
import {serverDirectionFor} from "@/utils/serverDirection";
import {tripService, isRouteHashError, isWalletDisabledError, isPaymentMethodUnusableError} from "@/services/tripService";
import {queryClient} from "@/lib/queryClient";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";
import {buildBookingPayloadParts} from "@/utils/bookingPayload";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {isNearAirport, getTripAirportIds} from "@/utils/airports";
import {setBookingResult} from "@/state/bookingResult.js";
import {isIdentityIncompleteError, identityMissingFromError} from "@/utils/identityGate";
import IdentityIncompleteNotice from "@/customComponents/uiComponents/global/IdentityIncompleteNotice.jsx";
import {filterSelectedCarDetails} from "@/utils/filesConstants.js";
import {comparePrices} from "@/utils/pricing.js";
import {dummyMap, peoplesIcon, fiveStars, verifiedBadge} from "@img/index.js";
import TripRouteMap from "@/components/map/TripRouteMap.jsx";
import {parseRideDate} from "@/utils/dateUtils.js";

// How long this step stays up once the booking succeeds.
//
// It used to be a flat 8s. The map intro now plays here (globe → dive → the green line
// filling the route), and on a cold tile load that can outlast 8s — so the advance waits
// for whichever finishes LAST: the minimum below, or the animation reporting in through
// `onIntroDone`. MAX is the backstop, so a map that never reports (no polyline, a dead
// Maps key, a tile fetch that hangs) can't strand a booking that already succeeded.
const MIN_PROCESSING_MS = 8000;
const MAX_PROCESSING_MS = 12000;

export function ProcessingBooking(props) {
    const [bookingError, setBookingError] = useState(false);
    // The identity fields the gate refused this booking for, or null. Held apart from
    // `bookingError` because it renders as a notice WITH a way out, not as error text.
    const [identityMissing, setIdentityMissing] = useState(null);
    // Where the back button lands after a failure. Payment-related failures go back to the
    // payment step; everything else to orderDetails.
    const [backTarget, setBackTarget] = useState("orderDetails");
    // Same route the banner draws (polyline + leg markers) — MAP_UI is still in
    // session at this point (bookingReceived is what wipes it).
    const [mapUi, setMapUi] = useState(() => storage.get(STORAGE_KEYS.MAP_UI, []));

    // Intro-animation handshake. Refs, not state: these are read from inside timers, and
    // re-rendering on them would restart nothing useful.
    const introDoneRef = useRef(false);
    const pendingAdvanceRef = useRef(null);   // advance held back, waiting on the intro
    const timersRef = useRef([]);

    // Called by the map when the green fill lands. If the minimum wait already elapsed,
    // this is what actually moves the step on.
    const handleIntroDone = () => {
        introDoneRef.current = true;
        const pending = pendingAdvanceRef.current;
        if (pending) {
            pendingAdvanceRef.current = null;
            pending();
        }
    };

    // Drop any pending advance if the step unmounts first.
    useEffect(() => () => timersRef.current.splice(0).forEach(clearTimeout), []);

    useEffect(() => {
        async function hopnSendBookingRequest() {
            const routeHash = storage.get(STORAGE_KEYS.ROUTE_HASH);
            const coupon = storage.get(STORAGE_KEYS.COUPON_VALUE);
            // Capped again HERE, not only in the form. A draft written to storage before
            // this cap existed reaches this line without ever passing through the form —
            // the passenger simply never reopens Special Request on the way to Reserve.
            // That is the path that produced "Special request cannot exceed 500
            // characters" at this very step.
            const rawSpacialRequest = storage.get(STORAGE_KEYS.SPECIAL_REQUEST);
            const spacialRequest = typeof rawSpacialRequest === 'string'
                ? rawSpacialRequest.slice(0, BOOKING_CONSTRAINTS.SPECIAL_REQUEST_MAX_LENGTH)
                : rawSpacialRequest;
            const flightNumber = storage.get(STORAGE_KEYS.FLIGHT_NUMBER) || null;
            const carId = storage.get(STORAGE_KEYS.SELECTED_CAR);
            const date = storage.get(STORAGE_KEYS.RIDE_DATE);
            const time = storage.get(STORAGE_KEYS.RIDE_TIME);

            const {passengers, childSeats: childSeatsPayload, luggage} = buildBookingPayloadParts();

            const formatDate = (d) => {
                if (!d) return '';
                const dt = parseRideDate(d);
                if (!dt) return '';
                return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
            };
            const formatTime = (t) => {
                if (!t) return '';
                const dt = new Date(t);
                return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}:00`;
            };

            const driverTip = storage.get(STORAGE_KEYS.DRIVER_TIP) || null;

            const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []);
            const firstDest = destinations[0];
            const lastDest = destinations[destinations.length - 1];
            const isPickupAirport = firstDest?.lat && firstDest?.lng ? !!isNearAirport(firstDest.lat, firstDest.lng) : false;
            const isDropOffAirport = lastDest?.lat && lastDest?.lng ? !!isNearAirport(lastDest.lat, lastDest.lng) : false;
            // The server's direction (switch on) wins; otherwise our own coordinate match.
            const tripDirection = serverDirectionFor(destinations[0], destinations[1])?.direction
                ?? (isPickupAirport ? 'from_airport' : isDropOffAirport ? 'to_airport' : 'others');

            const useWalletAmount = Number(storage.get(STORAGE_KEYS.USE_WALLET_AMOUNT)) || 0;

            const payload = {
                vehicle_type_id: carId,
                passenger_types: passengers,
                child_seats: childSeatsPayload,
                luggage,
                create_return_booking: storage.get(STORAGE_KEYS.RETURN_BOOKING) === true,
                flight_number: flightNumber,
                special_request: spacialRequest,
                having_pet: storage.get(STORAGE_KEYS.CARING_PET) === true,
                quiet_ride: storage.get(STORAGE_KEYS.QUIET_RIDE) === true,
                scheduled_at: `${formatDate(date)} ${formatTime(time)}`,
                discount_code: coupon,
                payment_method_id: storage.get(STORAGE_KEYS.USER_CARD_ID),
                route_hash: routeHash,
                gratuity_percentage: driverTip ? parseInt(driverTip) : null,
                trip_direction: tripDirection,
            };
            if (useWalletAmount > 0) payload.use_wallet_amount = useWalletAmount;

            // Which airport (and which terminal) this ride touches. Both come from
            // /config/airports via the same coordinate match that decided trip_direction
            // above, so they can't contradict it. Sent only when there IS an airport —
            // a city-to-city trip omits the fields rather than sending nulls.
            const {airport_id, airport_terminal_id} = getTripAirportIds();
            if (airport_id) payload.airport_id = airport_id;
            if (airport_terminal_id) payload.airport_terminal_id = airport_terminal_id;

            let bookingResult = await tripService.createTrip(payload);

            if (isRouteHashError(bookingResult)) {
                if (destinations.length >= 2) {
                    const origin = destinations[0];
                    const destination = destinations[1];
                    const stopOvers = destinations.slice(2);

                    const routeResult = await tripService.calculateRoute(origin, destination, stopOvers);
                    if (routeResult.success) {
                        const newHash = routeResult.data.route_hash ?? routeResult.data.routeHash;
                        const newRouteData = routeResult.data.route_data ?? routeResult.data.routeData;
                        storage.set(STORAGE_KEYS.ROUTE_HASH, newHash);
                        storage.set(STORAGE_KEYS.ROUTE_BREAKDOWN, newRouteData);
                        storage.set(STORAGE_KEYS.MAP_UI, newRouteData);
                        // Route was re-solved — redraw with the fresh geometry
                        setMapUi(newRouteData);
                        payload.route_hash = newHash;
                        bookingResult = await tripService.createTrip(payload);
                    }
                }
            }

            if (bookingResult.success && bookingResult.data?.trip) {
                // Held in memory only (gone on reload). trip_direction rides along
                // so bookingReceived can pick the right perk buttons without the
                // (about-to-be-wiped) destinations. See state/bookingResult.js.
                // Capture the Prebook-vs-Ride-Sharing figures NOW, while the selected
                // car + destinations are still in session (bookingReceived wipes them),
                // so the confirmation screen's compare section can render.
                const cmpCar = filterSelectedCarDetails() || {};
                // Both compare figures come from the car's breakdown while it's still
                // in session — the ride-share one is our total minus the online/coupon
                // discounts, so it can't be re-derived from `price` later.
                const {price: cmpPrice, sharePrice: cmpShare} = comparePrices(cmpCar);
                const cmpDests = storage.get(STORAGE_KEYS.DESTINATIONS, []) || [];
                const cmpLabel = (d) => {
                    if (!d) return '';
                    // Prefer the airport IATA code when near an airport (address often reads
                    // "YYZ Terminal 1, Mississauga…" → city fallback would wrongly show the city).
                    const near = d.lat && d.lng ? isNearAirport(d.lat, d.lng) : false;
                    if (near && near.code) return near.code;
                    const code = d.address?.match(/\(([A-Z]{3})\)/);
                    if (code) return code[1];
                    if (d.city) return d.city;
                    return (d.address || '').split(',')[0].trim();
                };
                const cmpDurMin = storage.get(STORAGE_KEYS.ROUTE_BREAKDOWN)?.duration_minutes || 0;
                const compare = {price: cmpPrice, share: cmpShare, pickup: cmpLabel(cmpDests[0]), dropoff: cmpLabel(cmpDests[1]), scheduledAt: payload.scheduled_at, durationMin: cmpDurMin};
                setBookingResult({...bookingResult.data, trip_direction: tripDirection, compare});
                // The trip EXISTS now — a reload must not restore orderDetails (the
                // step breadcrumbs still point there until bookingReceived mounts
                // 8s from now, and re-submitting could double-book). Drop them
                // immediately; bookingReceived wipes the rest of the session.
                storage.remove(STORAGE_KEYS.LATEST_STEP);
                storage.remove(STORAGE_KEYS.PREVIOUS_STEP);
                const advance = () => props.setStep("bookingReceived");
                // Nothing to wait for when there's no live map (the dummy-map fallback):
                // the intro can never report, so the minimum wait is the whole story.
                const hasAnimatedMap = !!storage.get(STORAGE_KEYS.MAP_UI)?.polyline;

                timersRef.current.push(setTimeout(() => {
                    if (introDoneRef.current || !hasAnimatedMap) advance();
                    else pendingAdvanceRef.current = advance;   // handleIntroDone fires it
                }, MIN_PROCESSING_MS));

                timersRef.current.push(setTimeout(() => {
                    if (pendingAdvanceRef.current) {
                        pendingAdvanceRef.current = null;
                        advance();
                    }
                }, MAX_PROCESSING_MS));
            } else if (isPaymentMethodUnusableError(bookingResult)) {
                // The saved card was verified at submit and refused (expired, deactivated,
                // or tokenized in the other Square environment). Forget the selection and
                // drop the cached card list — the listing filters unusable cards out, so a
                // refetch is what makes the bad one disappear from the payment step instead
                // of sitting there inviting a second failure.
                storage.remove(STORAGE_KEYS.USER_CARD_ID);
                queryClient.invalidateQueries({queryKey: ['payment-methods']});
                setBackTarget("paymentCards");
                setBookingError(bookingResult.raw?.message || "This saved card can't be used for this booking. Please choose another card or add a new one.");
            } else if (isWalletDisabledError(bookingResult)) {
                // Credits were switched off between the payment step and this submit (the
                // balance is cached, so the section can still have been on screen). Drop the
                // stored amount so a retry goes card-only, and send them back to payment
                // rather than to orderDetails — the amount they owe just changed.
                storage.remove(STORAGE_KEYS.USE_WALLET_AMOUNT);
                setBackTarget("paymentCards");
                setBookingError(bookingResult.raw?.message || "Hop'n Credits aren't available right now. Please continue with a card.");
            } else if (isIdentityIncompleteError(bookingResult)) {
                // The account is missing a field the gate requires — in practice the phone,
                // which Google and Apple never supply and a desk-created guest does not have
                // until they claim the booking.
                //
                // Shown as the notice rather than as `bookingError` text because this is a
                // FIXABLE refusal, not a failure: the notice carries the way out (to the
                // profile page), where a bare sentence under "Please check following errors"
                // would leave the passenger stuck with nothing to act on.
                setIdentityMissing(identityMissingFromError(bookingResult));
            } else {
                const rawResponse = bookingResult.raw || {};
                setBookingError(rawResponse.message || bookingResult.error || "Server is busy try again");
            }
        }

        hopnSendBookingRequest();
    }, []);

    return (
        <>
            {bookingError && (
                <ModalHeader
                    title=""
                    extraClasses="transparent"
                    totalSteps={0}
                    cancelButton={false}
                    backStep={backTarget}
                    showBackButton={true}
                    setStep={props.setStep}
                    closeModal={props.closeModal}
                />
            )}

            <div className="over-right-area">
                {mapUi?.polyline
                    ? <TripRouteMap polyline={mapUi.polyline} legs={mapUi.legs}
                                         animate minimal onIntroDone={handleIntroDone}/>
                    : <img src={dummyMap} alt="dummy Map"/>
                }
            </div>

            <div className="hopn-booking-processing modalPages-spacing padding-bottom-30">
                {identityMissing ? (
                    <IdentityIncompleteNotice missing={identityMissing}/>
                ) : bookingError ? (
                    <div className="booking-error"><h6>Please check following errors</h6>{bookingError}</div>
                ) : (
                    <div className="circle-rotate">
                        <img decoding="async" src={peoplesIcon} alt="People"/>
                    </div>
                )}

                <span className="title" hopn-data-category="ride_confirmed" hopn-data-index="title">
                        {hopnLanguageArray("ride_confirmed", "title", "We are processing your booking…")}
                    </span>

                <strong hopn-data-category="ride_confirmed" hopn-data-index="description">
                    {hopnLanguageArray("ride_confirmed", "description", "Over 8 million km of airport transfers by pro partners in 20 years")}
                </strong>

                <div className="text-center pt-5 flex flex-col items-center gap-3">
                    <img decoding="async" src={fiveStars}  alt="Five stars"/>
                    <img decoding="async" src={verifiedBadge} alt="Verified"/>
                </div>
            </div>
        </>
    );
}

export default ProcessingBooking;
